// src/components/layouts/app/header/mobile/MobileFullScreenSearch.tsx
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, ArrowLeft } from 'lucide-react';

interface MobileFullScreenSearchProps {
  isOpen: boolean;
  onClose: () => void;
}

const suggestions = ['MacBook Pro', 'PC Dell', 'Clavier mécanique', 'Souris gaming', 'Écran 24 pouces', 'Disque SSD'];

const MobileFullScreenSearch: React.FC<MobileFullScreenSearchProps> = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');

  const filteredSuggestions = suggestions.filter(s =>
    s.toLowerCase().includes(query.toLowerCase())
  );

  const handleSearch = () => {
    if (query.trim()) {
      console.log('Recherche :', query);
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[100] bg-white md:hidden"
        >
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 30, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 350 }}
            className="flex flex-col h-full"
          >
            {/* Header avec barre de recherche */}
            <div className="sticky top-0 bg-white border-b border-gray-100 px-4 py-3">
              <div className="flex items-center gap-3">
                <button
                  onClick={onClose}
                  className="p-1 -ml-1 text-gray-600 active:scale-95 transition-transform"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder="Rechercher un produit..."
                    className="w-full pl-9 pr-10 py-2.5 text-base border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                  {query && (
                    <button
                      onClick={() => setQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Contenu scrollable */}
            <div className="flex-1 overflow-y-auto p-4">
              {query === '' ? (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="space-y-4"
                >
                  <p className="text-sm text-gray-500">Recherches fréquentes</p>
                  <div className="flex flex-wrap gap-2">
                    {suggestions.slice(0, 5).map((s, idx) => (
                      <button
                        key={idx}
                        onClick={() => setQuery(s)}
                        className="px-3 py-1.5 text-sm bg-gray-100 rounded-full text-gray-700 active:scale-95 transition-transform"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="space-y-2"
                >
                  <p className="text-sm text-gray-500">Suggestions</p>
                  {filteredSuggestions.length > 0 ? (
                    filteredSuggestions.map((s, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          setQuery(s);
                          handleSearch();
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded-lg flex items-center gap-2 transition-colors"
                      >
                        <Search className="w-4 h-4 text-gray-400" />
                        <span>{s}</span>
                      </button>
                    ))
                  ) : (
                    <p className="text-center text-gray-400 py-8">Aucun résultat pour "{query}"</p>
                  )}
                </motion.div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MobileFullScreenSearch;